import { api, uploadProgress } from "@/api/client";
import { blobErrorMessage } from "@/lib/api-errors";

/**
 * «ОКК: входной контроль» — проверка прихода отделом качества
 * (право `okk_receiving`, бэкенд `okk_receiving/router.py`).
 *
 * Проверка заводится сама на «Завершить приёмку»; здесь — работа ОКК с ней:
 * вердикты по позициям, образцы, лента комментариев, фото, завершение.
 */

export type InspectionStatus = "new" | "in_progress" | "testing" | "done";
export type Verdict =
  | "in_work"
  | "conditional"
  | "testing"
  | "quarantine"
  | "return"
  | "not_required";
export type ReturnUnit = "pcs" | "places";

/** Автор: показывать `one_c_name || username` (`lib/okk-receiving.ts::authorName`). */
export interface UserRef {
  id: number;
  username: string;
  one_c_name: string | null;
}

export interface DeliveryHead {
  delivery_id: string;
  delivery_status: string;
  order_ref: string;
  order_number: string;
  incoming_number: string;
  incoming_date: string | null;
  upd_missing: boolean;
  organization_name: string | null;
  kontragent_name: string | null;
  one_c_receipt_number: string | null;
  receipt_posted: boolean;
  receiving_date: string | null;
  completed_at: string | null;
}

export interface InspectionRow {
  id: string;
  status: InspectionStatus;
  delivery: DeliveryHead;
  items_total: number;
  items_with_verdict: number;
  /** Вердикт → сколько позиций (только оставшиеся в приходе). */
  verdicts: Partial<Record<Verdict, number>>;
  nearest_test_until: string | null;
  /** Срок не настроен — `null` (не «в срок»). */
  overdue: boolean | null;
  photos_count: number;
  comments_count: number;
  /** Есть позиции с образцами «в архив» > 0 без документа 1С. */
  samples_pending: boolean;
  created_at: string;
  completed_at: string | null;
}

/** Счётчики вкладок: по поиску `q`, БЕЗ фильтра статуса. */
export interface StatusCounts {
  new: number;
  in_progress: number;
  testing: number;
  done: number;
  all: number;
}

export interface InspectionList {
  items: InspectionRow[];
  /** Всего по фильтру (с учётом статуса) — для пагинации. */
  total: number;
  counts: StatusCounts;
}

/** `ok` — доставлено; `error` — последний сбой (`error` — текст, события ждут
 *  «Повторить»); `pending` — события есть, доставки не было; `disabled` — Битрикс
 *  выключен; `no_task` — у прихода нет задачи ОКК. */
export type BitrixState = "ok" | "error" | "pending" | "disabled" | "no_task";
export type BitrixEvent = "link" | "testing" | "complete" | "reopen" | "samples";

/** Синк проверки с задачей ОКК в Битриксе (`okk_receiving/bitrix_sync.py`). */
export interface BitrixSyncView {
  state: BitrixState;
  error: string | null;
  pending: BitrixEvent[];
  synced_at: string | null;
  task_id: number | null;
}

export interface ItemView {
  id: string;
  batch_id: string | null;
  nomenclature_ref: string;
  nomenclature_name: string;
  article: string;
  characteristic_name: string;
  supplier_batch: string;
  one_c_partia_ref: string | null;
  qty: number;
  unit_name: string;
  group_path: string;
  /** `okk` | `unassigned` (группа номенклатуры не настроена). */
  target: string;
  removed_from_delivery: boolean;
  /** Почему позиция снята: `gone` — больше нет в приходе, `rerouted` — больше не на ОКК
   *  (изменилось правило маршрута). Старый бэкенд поле не шлёт. */
  removed_reason?: "gone" | "rerouted" | null;
  verdict: Verdict | null;
  verdict_note: string | null;
  test_until: string | null;
  return_qty: number | null;
  return_unit: ReturnUnit | null;
  /** Образцы «в архив» — в БАЗОВОЙ единице номенклатуры (её имя — в
   *  `StockSourcesView.unit_name` и в строках документа), не в `unit_name` позиции. */
  samples_archive_qty: number;
  samples_test_qty: number;
  /** Склад-источник образцов «в архив», выбранный ОКК; `null` — не выбран. */
  samples_source_warehouse: RefName | null;
  /** Оформлены документом 1С — кол-во «в архив» и склад больше не меняются (409). */
  samples_transferred: boolean;
  samples_transfer_id: string | null;
  samples_transfer_number: string | null;
  updated_by: UserRef | null;
  updated_at: string | null;
}

export interface RefName {
  ref: string;
  name: string;
}

export interface SampleTransferLine {
  item_id: string;
  nomenclature_ref: string;
  nomenclature_name: string;
  characteristic_ref: string | null;
  characteristic_name: string;
  partia_ref: string | null;
  partia_number: string | null;
  /** В базовой единице номенклатуры. */
  qty: number;
  unit_ref: string;
  unit_name: string;
}

/** Документ 1С `ПеремещениеЗапасов` «образцы ОКК в архив». */
export interface SampleTransferView {
  id: string;
  one_c_ref: string;
  one_c_number: string;
  source_warehouse: RefName;
  target_warehouse: RefName;
  /** Создан, но не проведён — повторное «Оформить» его проведёт. */
  posted: boolean;
  posted_at: string | null;
  created_at: string;
  created_by: UserRef | null;
  lines: SampleTransferLine[];
}

export interface StockSourceView {
  warehouse_ref: string;
  warehouse_name: string;
  /** Свободный остаток строки ПН на складе — в базовой единице. */
  free_qty: number;
}

/** Откуда можно взять образцы «в архив» позиции (живьём из 1С). */
export interface StockSourcesView {
  item_id: string;
  nomenclature_ref: string;
  characteristic_ref: string | null;
  partia_ref: string | null;
  partia_number: string | null;
  unit_ref: string;
  /** Базовая единица номенклатуры — в ней кол-во образцов «в архив». */
  unit_name: string;
  /** Больший остаток — первым. Пусто — свободного остатка нет нигде. */
  sources: StockSourceView[];
  selected_warehouse_ref: string | null;
}

export interface OkkSettings {
  /** Склад «Арбитражная» — куда уходят образцы «в архив»; `null` — не настроен. */
  arbitrage_warehouse: RefName | null;
}

export interface CommentView {
  id: string;
  item_id: string | null;
  author: UserRef | null;
  text: string;
  created_at: string;
}

export interface PhotoView {
  id: string;
  item_id: string | null;
  content_type: string;
  size_bytes: number;
  width: number;
  height: number;
  uploaded_by: UserRef | null;
  uploaded_at: string;
}

export interface ReceivingPhotoView {
  id: string;
  category: string;
  category_title: string;
  created_at: string;
  batch_id: string | null;
  place_id: string | null;
  place_seq: number | null;
}

export interface InspectionDetail {
  id: string;
  status: InspectionStatus;
  delivery: DeliveryHead;
  overdue: boolean | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  completed_by: UserRef | null;
  updated_at: string;
  items: ItemView[];
  comments: CommentView[];
  photos: PhotoView[];
  receiving_photos: ReceivingPhotoView[];
  samples_transfers: SampleTransferView[];
  samples_pending: boolean;
  /** Склад арбитража из настроек; `null` — не настроен (оформление ответит 409). */
  samples_target_warehouse: RefName | null;
  /** Есть во ВСЕХ ответах-карточках (GET, PATCH, complete, reopen, sample-transfers, bitrix-sync). */
  bitrix: BitrixSyncView;
}

/** Частичная правка: уходят только присланные поля; `verdict: null` — сброс.
 *  Числа — строкой с точкой (Decimal на сервере, без потерь float). */
export interface ItemPatch {
  verdict?: Verdict | null;
  verdict_note?: string | null;
  test_until?: string | null;
  return_qty?: string | null;
  return_unit?: ReturnUnit | null;
  samples_archive_qty?: string;
  samples_test_qty?: string;
  /** Склад из `getStockSources` (иначе 422); после оформления — 409. */
  samples_source_warehouse_ref?: string | null;
}

export const INSPECTION_PAGE = 50;

export async function listInspections(params: {
  status: InspectionStatus | null;
  q: string;
  offset: number;
  limit?: number;
}): Promise<InspectionList> {
  const { data } = await api.get<InspectionList>("/okk-receiving/inspections", {
    params: {
      status: params.status ?? undefined,
      q: params.q || undefined,
      offset: params.offset,
      limit: params.limit ?? INSPECTION_PAGE,
    },
  });
  return data;
}

export async function getInspection(id: string): Promise<InspectionDetail> {
  const { data } = await api.get<InspectionDetail>(`/okk-receiving/inspections/${id}`);
  return data;
}

export async function patchItem(
  inspectionId: string,
  itemId: string,
  body: ItemPatch,
): Promise<InspectionDetail> {
  const { data } = await api.patch<InspectionDetail>(
    `/okk-receiving/inspections/${inspectionId}/items/${itemId}`,
    body,
  );
  return data;
}

export async function addComment(
  inspectionId: string,
  body: { text: string; item_id: string | null },
): Promise<CommentView> {
  const { data } = await api.post<CommentView>(
    `/okk-receiving/inspections/${inspectionId}/comments`,
    { text: body.text, item_id: body.item_id ?? undefined },
  );
  return data;
}

export async function deleteComment(inspectionId: string, commentId: string): Promise<void> {
  await api.delete(`/okk-receiving/inspections/${inspectionId}/comments/${commentId}`);
}

/** Фото — ОРИГИНАЛОМ (без ужатия): ОКК разглядывает дефекты и мелкий текст. */
export async function uploadPhoto(
  inspectionId: string,
  file: Blob,
  opts: { itemId: string | null; fileName: string; onProgress?: (percent: number) => void },
): Promise<PhotoView> {
  const form = new FormData();
  form.append("file", file, opts.fileName);
  if (opts.itemId) form.append("item_id", opts.itemId);
  const { data } = await api.post<PhotoView>(
    `/okk-receiving/inspections/${inspectionId}/photos`,
    form,
    uploadProgress(opts.onProgress),
  );
  return data;
}

export async function deletePhoto(inspectionId: string, photoId: string): Promise<void> {
  await api.delete(`/okk-receiving/inspections/${inspectionId}/photos/${photoId}`);
}

/**
 * GET картинки как blob → object URL. При ошибке axios отдаёт тело как Blob —
 * текст сервера достаём из него, иначе вместо причины (404 «фото удалено»,
 * 502 …) остаётся «Request failed with status code …».
 */
async function fetchPhotoUrl(url: string, thumb: boolean): Promise<string> {
  let blob: Blob;
  try {
    const resp = await api.get<Blob>(url, {
      params: thumb ? { size: "thumb" } : undefined,
      responseType: "blob",
    });
    blob = resp.data;
  } catch (e) {
    throw new Error(await blobErrorMessage(e, "Не удалось загрузить фото"));
  }
  return URL.createObjectURL(blob);
}

/** Фото проверки (auth — Bearer) → object URL; вызывающий делает revoke. */
export async function fetchOkkPhotoUrl(photoId: string, thumb: boolean): Promise<string> {
  return fetchPhotoUrl(`/okk-receiving/photos/${photoId}`, thumb);
}

/** Фото приёмки этого прихода (скан УПД, сертификаты, товар) — через прокси раздела. */
export async function fetchReceivingPhotoUrl(
  inspectionId: string,
  photoId: string,
  thumb: boolean,
): Promise<string> {
  return fetchPhotoUrl(
    `/okk-receiving/inspections/${inspectionId}/receiving-photos/${photoId}`,
    thumb,
  );
}

export async function completeInspection(id: string): Promise<InspectionDetail> {
  const { data } = await api.post<InspectionDetail>(`/okk-receiving/inspections/${id}/complete`);
  return data;
}

export async function reopenInspection(id: string): Promise<InspectionDetail> {
  const { data } = await api.post<InspectionDetail>(`/okk-receiving/inspections/${id}/reopen`);
  return data;
}

/** Склады организации прихода со свободным остатком строки ПН позиции.
 *  Сбой 1С — 502 с текстом (не пустой список). */
export async function getStockSources(
  inspectionId: string,
  itemId: string,
): Promise<StockSourcesView> {
  const { data } = await api.get<StockSourcesView>(
    `/okk-receiving/inspections/${inspectionId}/items/${itemId}/stock-sources`,
  );
  return data;
}

/** Оформить образцы «в архив» (по документу на склад-источник) или провести
 *  созданные, но не проведённые. Гейты — 409 с текстом; сбой проведения — 502. */
export async function createSampleTransfers(inspectionId: string): Promise<InspectionDetail> {
  const { data } = await api.post<InspectionDetail>(
    `/okk-receiving/inspections/${inspectionId}/sample-transfers`,
  );
  return data;
}

/** «Повторить» доставку в Битрикс. Сбой доставки — НЕ HTTP-ошибка, а `bitrix.state=error`. */
export async function syncBitrix(inspectionId: string): Promise<InspectionDetail> {
  const { data } = await api.post<InspectionDetail>(
    `/okk-receiving/inspections/${inspectionId}/bitrix-sync`,
  );
  return data;
}

export async function getOkkSettings(): Promise<OkkSettings> {
  const { data } = await api.get<OkkSettings>("/okk-receiving/settings");
  return data;
}

/** Склад проверяется в 1С (существует, «Склад», не помечен) → иначе 422; `null` — очистить. */
export async function updateOkkSettings(body: {
  arbitrage_warehouse_ref: string | null;
}): Promise<OkkSettings> {
  const { data } = await api.put<OkkSettings>("/okk-receiving/settings", body);
  return data;
}
